import { useCallback, useEffect, useRef, useState } from "react";
import { fetchRadarHealth } from "../weather/radarHealthApi.js";
import { fetchAerocivilRadarHealth } from "../weather/adminRadarActionsApi.js";

const AUTO_REFRESH_MS = 60_000;

export function useAdminWeatherRadarHealth({ sourceMode = "preferred", autoRefresh = true } = {}) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const requestIdRef = useRef(0);

  const loadHealth = useCallback(
    async ({ refresh = false, silent = false } = {}) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      if (!silent) {
        if (refresh) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }
      }

      setErrorMessage("");

      try {
        const [payload, aerocivilHealth] = await Promise.all([
          fetchRadarHealth({ sourceMode, refresh }),
          fetchAerocivilRadarHealth({ refresh }).catch(() => null),
        ]);
        if (requestIdRef.current !== requestId) {
          return;
        }

        setHealth({
          ...payload,
          sources: {
            ...payload.sources,
            aerocivilAviation: aerocivilHealth,
          },
        });
        setLastRefreshedAt(new Date().toISOString());
      } catch (error) {
        if (requestIdRef.current !== requestId) {
          return;
        }

        setHealth(null);
        setErrorMessage(error.message || "Weather health endpoint unavailable.");
      } finally {
        if (requestIdRef.current === requestId) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [sourceMode]
  );

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  useEffect(() => {
    if (!autoRefresh) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      loadHealth({ refresh: true, silent: true });
    }, AUTO_REFRESH_MS);

    return () => window.clearInterval(intervalId);
  }, [autoRefresh, loadHealth]);

  const refreshHealth = useCallback((options = {}) => {
    return loadHealth({ refresh: true, ...options });
  }, [loadHealth]);

  return {
    health,
    loading,
    refreshing,
    errorMessage,
    lastRefreshedAt,
    refreshHealth,
  };
}
